import dayjs from 'dayjs';
import { useRouter } from '@application/utils';
import type { MtLayoutData } from '@presentation/components';
import { CACHE_KEY_BREEDER_STORE_PARTNERSHIP_ALERT_BAR_PERSIST_DATE } from '@application/constants';
import { useCallback, useMemo, useState } from 'react';
import type { UseBreederPartnershipAlertBarParams } from './types';
import { useCurrentProfileStep } from '../../pages/MtBreederOnboardingPageController/api/useCurrentProfileStep';
import { BREEDER_PARTNERSHIP_ALERT_PERSIST_HOURS } from './constants';

export const useBreederPartnershipAlertBar = (params: UseBreederPartnershipAlertBarParams) => {
  const { currentAccountType } = params;

  const [showPartnershipAlertBar, setShowPartnershipAlertBar] = useState(true);

  const { translate, routeLocationDescriptor } = useRouter();

  const { profileValidated, profileLoading } = useCurrentProfileStep();

  const persistLocalStorage = useCallback(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(
      CACHE_KEY_BREEDER_STORE_PARTNERSHIP_ALERT_BAR_PERSIST_DATE,
      new Date().getTime().toString(),
    );
  }, []);

  const partnershipAlertBar = useMemo((): MtLayoutData['partnershipAlertBar'] => {
    if (typeof window === 'undefined') return; // Dont' show if server rendered
    if (!showPartnershipAlertBar) return;
    if (currentAccountType === 'buyer') return;
    if (!profileValidated || profileLoading) return;
    let shouldShow = true;
    const timestamp = localStorage.getItem(
      CACHE_KEY_BREEDER_STORE_PARTNERSHIP_ALERT_BAR_PERSIST_DATE,
    );

    if (timestamp) {
      const date = dayjs(Number(timestamp));
      shouldShow = +dayjs() >= +date.add(BREEDER_PARTNERSHIP_ALERT_PERSIST_HOURS, 'hours'); // Show if currentTime >= PERSIST_TIME + date
    }
    if (!shouldShow) return;

    const link = routeLocationDescriptor('mtAdvantages');

    return {
      title: translate('mt.breeder.partnershipAlertBar.title'),
      content: translate('mt.breeder.partnershipAlertBar.text'),
      linkText: translate('mt.breeder.partnershipAlertBar.cta'),
      link,
      onClick: () => {
        setShowPartnershipAlertBar(false);
        persistLocalStorage();
      },
      onDismissClick: () => {
        setShowPartnershipAlertBar(false);
        persistLocalStorage();
      },
      theme: 'success',
    };
  }, [
    showPartnershipAlertBar,
    currentAccountType,
    profileValidated,
    profileLoading,
    routeLocationDescriptor,
    translate,
    persistLocalStorage,
  ]);

  return {
    partnershipAlertBar,
  };
};
